/* eslint-disable sonarjs/no-identical-functions */
'use client'

import {
  useState,
  useEffect,
  useRef,
  type SyntheticEvent,
  useCallback,
} from 'react'
import {
  FormControl,
  FormGroup,
  MenuItem,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
  Grow,
  MenuList,
  Paper,
  Popper,
} from '@mui/material'
import { Button } from '@/components/library/button'
import { usePaginationContext } from '../utility/pagination/pagination-context'
import { useRouter } from 'next/navigation'
import {
  type FilterNameType,
  useGetRedirectPath,
} from '@/components/utility/product-filters/product-filters'
import xor from 'lodash/xor'

interface ICheckboxFilterProps {
  options: Array<{
    label: string
    value: string
  }>
  title: string
  selected?: string[]
  onFilterSelect?: (id: string) => void
  filterPath: FilterNameType
}

export const CheckboxFilterSmall = ({
  options,
  selected,
  filterPath,
}: {
  options: Array<{
    label: string
    value: string | number
  }>
  selected: string[]
  filterPath: FilterNameType
}) => {
  const { setPage } = usePaginationContext()
  const { getRedirectPath } = useGetRedirectPath()
  const router = useRouter()
  const handleSelected = (id: string) => {
    setPage(1)

    const updatedRetailersFilter = xor(selected, [id])
    const redirectPath = getRedirectPath(updatedRetailersFilter, filterPath)
    router.push(redirectPath, {
      scroll: false,
    })
  }
  const sortOptions = options.sort((a, b) => a.label.localeCompare(b.label))
  return (
    <FormControl
      sx={{ margin: '0 24px 0 24px' }}
      component='fieldset'
      variant='standard'
    >
      <FormGroup>
        {sortOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={selected.includes(option.value as string)}
                onChange={(e) => {
                  handleSelected(option.value as string)
                }}
                name={option.label}
                className='p-1'
              />
            }
            label={option.label}
            sx={{
              '& .MuiCheckbox-root': {
                color: '#713FFF !important',
              },
            }}
          />
        ))}
      </FormGroup>
    </FormControl>
  )
}

export const CheckboxFilter = (props: ICheckboxFilterProps) => {
  const { setPage } = usePaginationContext()
  const { getRedirectPath } = useGetRedirectPath()
  const router = useRouter()
  const handleSelected = (id: string) => {
    setPage(1)

    const updatedFilter = xor(props.selected, [id])
    const redirectPath = getRedirectPath(updatedFilter, props.filterPath)
    router.push(redirectPath, {
      scroll: false,
    })
  }

  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event | SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  function handleListKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current?.focus()
    }

    prevOpen.current = open
  }, [open])

  const setPopperHeight = useCallback(() => {
    if (typeof window === 'undefined') return '0px'
    const body = Math.max(document.body.scrollHeight)
    const offset = anchorRef?.current
      ? window.pageYOffset + anchorRef?.current.getBoundingClientRect().top
      : 0
    return `${body - offset - 50}px`
  }, [anchorRef])

  const sortOptions = props.options.sort((a, b) =>
    a.label.localeCompare(b.label),
  )

  return (
    <>
      <Button
        ref={anchorRef}
        id='checkbox-filter-button'
        aria-controls={open ? 'checkbox-filter-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        data-testid='filter-title'
        variant='text'
        color='primary'
        size='medium'
        className='ml-0 min-w-16 text-primary-dark no-underline'
      >
        {props?.title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement='bottom-start'
        style={{
          position: 'relative',
          zIndex: 999,
          maxHeight: setPopperHeight(),
          overflow: 'auto',
        }}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id='composition-menu'
                  aria-labelledby='composition-button'
                  onKeyDown={() => handleListKeyDown}
                >
                  <FormGroup>
                    {sortOptions.map((option, index) => (
                      <MenuItem
                        key={index}
                        onClick={() => {
                          handleSelected(option.value)
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={props.selected?.includes(option.value)}
                              onClick={() => {
                                handleSelected(option.value)
                              }}
                              name={option.label}
                              className='p-1'
                            />
                          }
                          label={option.label}
                          onClick={() => {
                            handleSelected(option.value)
                          }}
                          sx={{
                            '& .MuiCheckbox-root': {
                              color: '#713FFF !important',
                            },
                          }}
                        />
                      </MenuItem>
                    ))}
                  </FormGroup>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
